/**
 * A composição da grade do painel — issue #443.
 *
 * O painel desenhava uma grade só, com os blocos em ordem estrita de peso e cada formato com a
 * sua largura: cartão de 3 colunas, canal e fila de 4, lista e painel largo de 6. Larguras
 * diferentes em sequência deixam sobra em quase toda linha — medido na Plataforma, com 14
 * blocos numa grade de 1232px: 233px numa linha, 338px na seguinte e 628px numa terceira, que
 * ficou com metade da tela em branco. E como a linha do Grid tem a altura do bloco mais alto,
 * um cartão de 105px era esticado para 217px só para acompanhar o vizinho.
 *
 * A tela passa a ter três grupos, nesta ordem, e um grupo nunca divide linha com outro:
 *
 * 1. **Avisos** — os blocos de lista, que são os que apontam pendência. Abrem a tela sempre,
 *    e isso não depende de peso: peso ordena dentro do grupo, não entre grupos.
 * 2. **KPIs** — os cartões de um número só. O grupo ocupa a largura inteira, em uma ou duas
 *    linhas, e a largura de cada cartão sai de quantos eles são.
 * 3. **Painéis** — funil, medidor, tabela, canal e fila, dois por linha.
 *
 * Aqui ficam a divisão em linhas e o estilo de cada linha: a divisão é a parte que se mede sem
 * CSS, e o estilo é o que a tela aplica sem reescrever.
 */

import type { SxProps, Theme } from '@mui/material';

import type { BlocoMontado } from '../../types/dashboard';
import { ordenarBlocos } from './components/BlocoDoPainel';

export type GrupoDoPainel = 'avisos' | 'kpis' | 'paineis';

/** Uma linha da tela: os blocos que ela leva e o grupo a que pertencem, que dá a largura. */
export interface LinhaDoPainel {
  grupo: GrupoDoPainel;
  blocos: BlocoMontado[];
}

/** Os avisos abrem a tela, os KPIs vêm no meio, os painéis fecham. */
const ORDEM_DOS_GRUPOS: GrupoDoPainel[] = ['avisos', 'kpis', 'paineis'];

/** Dois por linha nos grupos de largura fixa — o KPI tem regra própria. */
const BLOCOS_POR_LINHA = 2;

/** No máximo duas linhas de KPI: passando disso o cartão fica estreito demais para o número. */
const LINHAS_DE_KPI = 2;

/** Acima disso o cartão de um número só fica estreito demais, e a linha vira uma régua. */
const KPIS_POR_LINHA = 4;

const grupoDoBloco = (bloco: BlocoMontado): GrupoDoPainel => {
  if (bloco.tipo === 'lista') return 'avisos';
  return bloco.tipo === 'cartao' ? 'kpis' : 'paineis';
};

const emLinhas = (blocos: readonly BlocoMontado[], tamanhos: readonly number[]): BlocoMontado[][] => {
  let posicao = 0;
  return tamanhos.map((tamanho) => {
    const linha = blocos.slice(posicao, posicao + tamanho);
    posicao += tamanho;
    return linha;
  });
};

/**
 * Quantos KPIs ficam em cada linha. Cinco viram 3 e 2, e não 4 e 1: as duas linhas preenchem a
 * largura inteira, e é isso que impede a sobra à direita do grupo.
 */
const linhasDeKpi = (total: number): number[] => {
  const linhas = Math.min(Math.ceil(total / KPIS_POR_LINHA), LINHAS_DE_KPI);
  const base = Math.floor(total / linhas);
  const sobrando = total % linhas;
  return Array.from({ length: linhas }, (_, indice) => base + (indice < sobrando ? 1 : 0));
};

/** Largura fixa: os blocos entram de dois em dois, e o último sozinho continua com a metade. */
const linhasDeMetade = (total: number): number[] =>
  Array.from({ length: Math.ceil(total / BLOCOS_POR_LINHA) }, (_, indice) =>
    Math.min(BLOCOS_POR_LINHA, total - indice * BLOCOS_POR_LINHA)
  );

/** As linhas do painel, de cima para baixo. */
export const comporGrade = (blocos: readonly BlocoMontado[]): LinhaDoPainel[] => {
  const ordenados = ordenarBlocos(blocos);

  return ORDEM_DOS_GRUPOS.flatMap((grupo) => {
    const doGrupo = ordenados.filter((bloco) => grupoDoBloco(bloco) === grupo);
    if (doGrupo.length === 0) return [];

    const tamanhos = grupo === 'kpis' ? linhasDeKpi(doGrupo.length) : linhasDeMetade(doGrupo.length);
    return emLinhas(doGrupo, tamanhos).map((daLinha) => ({ grupo, blocos: daLinha }));
  });
};

/**
 * A linha, com todos os cards na mesma altura: a do mais alto.
 *
 * Alinhados pelo topo, uma listagem vazia de 82px ficava ao lado de um painel de 217px e
 * deixava o degrau visível — metade da linha em branco. `stretch` faz o item da linha receber a
 * altura da linha inteira; passá-la ao card é trabalho de quem desenha o card.
 */
export const ESTILO_DA_LINHA = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'stretch',
  gap: 3,
} satisfies SxProps<Theme>;

/** O desconto de 12px é o meio do vão de 24px que separa os dois blocos da linha. */
const MEIO_DO_VAO = '12px';

/**
 * O item que segura um bloco na linha.
 *
 * O grupo de KPIs divide a largura inteira entre os cartões da linha — três numa e dois na
 * seguinte preenchem as duas. Os demais ficam com metade fixa, e o último continua com a metade
 * quando fica sozinho: esticá-lo até a borda daria a um bloco um destaque que o peso dele não
 * pediu.
 *
 * `display: flex` é o que faz a altura recebida da linha chegar ao card em vez de parar aqui —
 * item esticado com um card de altura automática dentro não muda nada na tela.
 */
export const estiloDoItem = (grupo: GrupoDoPainel) => ({
  display: 'flex',
  ...(grupo === 'kpis'
    ? { width: '100%', minWidth: 0, flex: { md: '1 1 0' } }
    : { width: { xs: '100%', md: `calc(50% - ${MEIO_DO_VAO})` }, flexShrink: 0 }),
});

export default comporGrade;
